import { useMemo, type ReactNode } from 'react';
import { useParentSize } from '@visx/responsive';
import type { SeriesBySensor, TelemetryVital } from '@/apis/telemetry';
import { chartModel } from '../lib/chartModel';
import type { Device } from '../lib/devices';
import { eventNear, eventsIn, type ShippingEvent } from '../lib/events';
import type { Extent } from '../lib/extent';
import type { RangeView } from '../lib/range';
import { stackGeometry } from '../lib/layout';
import { formatReadingTime } from '../lib/time';
import { useCrosshair } from '../useCrosshair';
import { useLabelMeasure } from '../useLabelMeasure';
import { useRangeSelection, type RangeSelection } from '../useRangeSelection';
import { ChunkStatus } from '../queries';
import CombinedTooltip from './CombinedTooltip';
import EventCard from './EventCard';
import VitalChart from './VitalChart';
import styles from './ChartStack.module.css';

/** Props for {@link ChartStack}. */
export interface ChartStackProps {
  charts: readonly TelemetryVital[];
  chartStatus: Partial<Record<TelemetryVital, ChunkStatus>>;
  devices: readonly Device[];
  series: SeriesBySensor;
  view: RangeView;
  notLoaded: Partial<Record<TelemetryVital, Extent[]>>;
  hidden: ReadonlySet<string>;
  timeZone: string;
  events: readonly ShippingEvent[];
  showEvents: boolean;
  renderReport?: (range: RangeSelection) => ReactNode;
  focusChart?: TelemetryVital | null;
  onChartFocused?: () => void;
}

const NONE: Extent[] = [];
const NO_EVENTS: ShippingEvent[] = [];

interface SizedStackProps extends ChartStackProps {
  width: number;
  range: RangeSelection;
}

const SizedStack = ({
  charts,
  chartStatus,
  devices,
  series,
  view,
  notLoaded,
  hidden,
  timeZone,
  events,
  showEvents,
  width,
  range,
  focusChart,
  onChartFocused,
}: SizedStackProps) => {
  const { window } = range;
  const models = useMemo(
    () => charts.map((key) => chartModel(key, devices, series, window, hidden)),
    [charts, devices, series, window, hidden]
  );
  const measure = useLabelMeasure();
  const { yAxes, marginLeft, xScale, xTicks } = useMemo(
    () => stackGeometry(models, window, width, timeZone, measure),
    [models, window, width, timeZone, measure]
  );
  const crosshair = useCrosshair(models, xScale);
  const { hover } = crosshair;
  const shownEvents = useMemo(
    () => (showEvents ? eventsIn(events, window) : NO_EVENTS),
    [showEvents, events, window]
  );
  const activeEvent =
    hover && showEvents
      ? eventNear(shownEvents, xScale(hover.pointer), xScale)
      : null;
  const shown = useMemo(
    () => devices.filter((device) => !hidden.has(device.id)),
    [devices, hidden]
  );

  return (
    <>
      {models.map((model, i) => (
        <VitalChart
          key={model.measure.key}
          model={model}
          status={chartStatus[model.measure.key] ?? ChunkStatus.Loading}
          width={width}
          marginLeft={marginLeft}
          xScale={xScale}
          xTicks={xTicks}
          yAxis={yAxes[i]}
          marked={view.marked && !range.zoomed}
          selection={range.selection}
          notLoaded={notLoaded[model.measure.key] ?? NONE}
          origins={range.origins}
          labelOrigins={i === 0 && view.marked}
          note={i === 0 ? range.note : null}
          handles={
            range.handles
              ? {
                  selection: range.selection,
                  selectable: range.selectable,
                  focusable: i === 0,
                  valueText: (ms) => formatReadingTime(ms, timeZone),
                  onMove: range.move,
                  onZoom: () => range.setZoomed(true),
                }
              : null
          }
          onZoomOut={range.zoomed ? () => range.setZoomed(false) : null}
          hoverAt={hover && !showEvents ? hover.at : null}
          events={shownEvents}
          activeEventId={activeEvent?.id ?? null}
          onHover={crosshair.move}
          onHoverEnd={crosshair.leave}
          focused={model.measure.key === focusChart}
          onFocused={onChartFocused}
        />
      ))}
      {activeEvent && hover && (
        <EventCard
          event={activeEvent}
          clientX={hover.clientX}
          clientY={hover.clientY}
          timeZone={timeZone}
        />
      )}
      {hover && !showEvents && (
        <CombinedTooltip
          hover={hover}
          models={models}
          devices={shown}
          timeZone={timeZone}
        />
      )}
    </>
  );
};

/**
 * The selected charts stacked vertically, all sharing one time axis and one crosshair and tooltip across the
 * range's window, or across the selection when zoomed. With `showEvents`, every chart draws the shipping events and
 * the tooltip gives way to an event's card while the pointer is within 5 px of its line (PRD §4.1). In the full range one pair of handles is shared by every chart; the stack is remounted
 * per range, which puts them back at pickup and delivery and zooms out. `renderReport` gets the range as shown, so a
 * report can print the window, selection and zoom the user is looking at. The `focusChart` chart scrolls into view and
 * takes focus, then `onChartFocused` is called.
 */
const ChartStack = (props: ChartStackProps) => {
  const range = useRangeSelection(props.view, props.notLoaded, props.timeZone);
  const { parentRef, width } = useParentSize({
    debounceTime: 50,
    ignoreDimensions: ['height', 'top', 'left'],
  });
  return (
    <div ref={parentRef} className={styles.stack}>
      {width > 0 && <SizedStack {...props} width={width} range={range} />}
      {props.renderReport?.(range)}
    </div>
  );
};

export default ChartStack;
